import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  Typography,
  Alert
} from '@mui/material';
import { departmentApi, Department } from '../services/api';

interface DepartmentFilterProps {
  selectedDepartments: number[];
  onDepartmentChange: (departmentIds: number[]) => void;
  userDepartments?: Department[];
}

const DepartmentFilter: React.FC<DepartmentFilterProps> = ({
  selectedDepartments,
  onDepartmentChange,
  userDepartments
}) => {
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        setLoading(true);
        setError(null);
        const departments = await departmentApi.getAllDepartments();
        setAllDepartments(departments);
      } catch (err) {
        console.error('加载部门列表失败:', err);
        setError('加载部门列表失败');
      } finally {
        setLoading(false);
      }
    };

    loadDepartments();
  }, []);

  const handleChange = (event: SelectChangeEvent<typeof selectedDepartments>) => {
    const value = event.target.value;
    let departmentIds: number[] = [];
    if (typeof value === 'string') {
      // Autocomplete autofill may return a comma-separated string
      departmentIds = value
        .split(',')
        .map(v => v.trim())
        .filter(v => v.length > 0)
        .map(v => Number(v));
    } else {
      departmentIds = (value as Array<string | number>).map(v => Number(v));
    }
    // De-duplicate and ensure numeric IDs
    onDepartmentChange(Array.from(new Set(departmentIds)));
  };

  // 确定要显示的部门列表
  // 如果提供了用户部门，优先使用用户部门；否则使用所有部门
  const displayDepartments = userDepartments && userDepartments.length > 0 ? userDepartments : allDepartments;

  if (loading) {
    return <Typography variant="body2" color="text.secondary">加载部门列表中...</Typography>;
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
          <InputLabel id="department-filter-label">部门筛选</InputLabel>
          <Select
            labelId="department-filter-label"
            multiple
            value={selectedDepartments}
            onChange={handleChange}
            input={<OutlinedInput label="部门筛选" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    全部部门
                  </Typography>
                ) : (
                  selected.map((value) => {
                    const department = displayDepartments.find(dept => dept.id === value);
                    return (
                      <Chip 
                        key={value} 
                        label={department?.name || `部门${value}`} 
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    );
                  })
                )}
              </Box>
            )}
          >
            {displayDepartments.map((department) => (
              <MenuItem key={department.id} value={department.id}>
                {department.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        

      </Box>
      

    </Box>
  );
};

export default DepartmentFilter;
